import { useState } from "react";

import { useBackend } from "../app/BackendProvider";
import { formatDuration, formatIsoTime } from "../app/format";
import { useNetworkId } from "../config/ConfigProvider";
import { describeError, useResource } from "../app/useResource";
import type { ToolsScope } from "./toolsScope";

const WITHIN = [15, 30, 60, 120] as const;

/**
 * Outil « prochaine fin de service » (P2-15, contrat v1.19). « Qui va se
 * libérer » — quel que soit le motif (fin de service, retour dépôt, relève
 * finale). Croise course active ↔ heure de fin de la sortie ; seules les
 * courses rapprochées (`runId`) y figurent. Consultation pure.
 *
 * Portée « véhicule » : ne garde que la ligne de ce véhicule.
 */
export function EndOfServicePanel({ scope }: { scope: ToolsScope }) {
  const backend = useBackend();
  const networkId = useNetworkId();
  const [within, setWithin] = useState<number>(30);

  const query = useResource(
    (signal) => backend.api.getEndOfService(networkId, { within }, signal),
    [backend, networkId, within],
  );
  const all = query.data?.items ?? [];
  const journeyId = scope.kind === "vehicle" ? scope.journey.id : null;
  const items = journeyId ? all.filter((it) => it.journeyId === journeyId) : all;

  return (
    <div>
      <div className="field has-addons" style={{ marginBottom: 8 }}>
        {WITHIN.map((m) => (
          <p className="control" key={m}>
            <button
              type="button"
              className={`button is-small${within === m ? " is-link" : ""}`}
              onClick={() => setWithin(m)}
              aria-pressed={within === m}
            >
              {m < 60 ? `${m} min` : `${m / 60} h`}
            </button>
          </p>
        ))}
      </div>

      {query.loading && !query.data ? (
        <p className="has-text-grey is-size-7">Chargement…</p>
      ) : query.error ? (
        <p className="has-text-danger is-size-7">{describeError(query.error)}</p>
      ) : items.length === 0 ? (
        <p className="has-text-grey is-size-7">
          {journeyId
            ? "Ce véhicule ne se libère pas dans ce délai (ou sa sortie n'est pas encore rapprochée)."
            : "Aucun véhicule ne se libère dans ce délai."}
        </p>
      ) : (
        <table className="table is-fullwidth is-narrow" style={{ fontSize: "11px", background: "var(--sentinelle-surface)" }}>
          <thead>
            <tr>
              <th>Dans</th>
              <th>Fin</th>
              <th>N° bus</th>
              <th>Ligne</th>
              <th>Sortie</th>
              <th>Service</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.journeyId}>
                <td className="mono" style={it.inSeconds <= 0 ? { color: "var(--sentinelle-signal)", fontWeight: 700 } : undefined}>
                  {it.inSeconds <= 0 ? "à quai" : formatDuration(it.inSeconds)}
                </td>
                <td className="mono has-text-grey">{formatIsoTime(it.endsAt)}</td>
                <td className="mono">{it.vehicleDisplayNumber ?? "—"}</td>
                <td className="mono">{it.lineCode}</td>
                <td className="mono">{it.runId ?? "—"}</td>
                <td className="mono">
                  {it.groupLetters ? `${it.groupLetters} ` : ""}
                  {it.serviceNumber}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
